import { addDays, asArray, isDateGreater, isDateLess, isRealDate, toInputDate } from './promoPrimitives';
import { getOffer, newRow, refOfOfferInfo } from './promoModel';
import { hasValidOfferDates } from './promoChecks';
import { refreshProductValues } from './promoValues';
import { forecastKeepsDatesEditable } from './promoForecast';

// Local operations ("OP locale" / pirate, #23521, spec §9, RG-20 → RG-23) : the sales rep creates and removes the offers,
// sets their dates, an internal comment, and adds the products through the product picker. Port of
// visitStep3-promo.js:2280-2349 and 3640-3885.

export const EMPTY_FILE_UID = '00000000-0000-0000-0000-000000000000';

/** "Ajouter une offre" is only offered while the campaign's nego period is not over (`canAddLocaleOffer`, JS:1384). */
export const canAddLocalOffer = (snapshot, campaign) => !snapshot?.readonly && !snapshot?.consultation && isDateGreater(campaign?.dates?.negoEnd, snapshot?.now);

/** New offers picked in the legacy modal (`__newLocaleOffer`, JS:2280) : they start tomorrow, without end date. */
export function createLocalOffers(snapshot, campaign, offerIds, newKey) {
	const startDate = new Date(toInputDate(addDays(snapshot.now, 1)));
	return asArray(offerIds).map((campaignOfferId) => ({
		_key: newKey(),
		campaignId: campaign.id,
		campaignOfferId,
		fileUid: EMPTY_FILE_UID,
		fileUid2: EMPTY_FILE_UID,
		comment: '',
		local: true,
		startDate,
		endDate: '',
	}));
}

/** Offers the local offer picker lists (`localeCampaignOfferVM.modalInit`) : the published offers of the campaign. */
export const localOfferCandidates = (campaign) => asArray(campaign?.offers).filter((offer) => offer.published);

/**
 * Products the picker lists for a local offer (`openProductPicker`, then `productsVM.modalInit`) : the published products
 * of the offer that the user may pick (product trees and categories in write, published in the assortment), minus the
 * products already in the offer.
 */
export function localProductCandidates(snapshot, campaign, offerInfo) {
	const offer = getOffer(campaign, offerInfo?.campaignOfferId);
	const taken = new Set(asArray(offerInfo?._negociatedCampaignOffers).map((row) => row.productId));
	const ids = [
		...new Set(
			asArray(offer?.products)
				.filter((p) => p.published)
				.map((p) => p.productId)
		),
	];
	return ids
		.filter((id) => !taken.has(id) && snapshot?.products?.[id]?.pickable)
		.map((id) => {
			const product = snapshot.products[id];
			return { id, name: product.label || '', ean: product.ean || '', description: product.description || '', imageThumbnailUid: product.imageThumbnailUid || null };
		});
}

/** Products are added while the offer is still before its store period (RG-23, `#btnAddPdt`, JS:3735). */
export const canAddLocalProducts = (snapshot, offerInfo) => !snapshot?.readonly && isDateGreater(offerInfo?.startDate, snapshot?.now);

/** Adds the picked products to the offer (`__productOfferPromoLocale`, JS:2317), each with an empty paying line. */
export function addLocalProducts(snapshot, campaign, offerInfo, productIds) {
	const ref = refOfOfferInfo(offerInfo);
	let rows = asArray(offerInfo._negociatedCampaignOffers).map((row) => ({ ...row }));
	asArray(productIds).forEach((productId) => {
		if (!productId) return;
		if (rows.some((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId)) return;
		rows = refreshProductValues(campaign, [...rows, newRow(ref, productId, { offerKey: ref.offerKey, local: true })], ref, productId);
	});
	return { ok: true, ref, negotiated: rows };
}

/** Removes a product from the offer : its paying line, additional presences and free line (the legacy left the free line behind). */
export function removeLocalProduct(snapshot, offerInfo, productId) {
	if (snapshot?.readonly) return { ok: false, error: 'PromoReadonly' };
	const ref = refOfOfferInfo(offerInfo);
	return {
		ok: true,
		ref,
		negotiated: asArray(offerInfo._negociatedCampaignOffers)
			.filter((row) => row.productId !== productId)
			.map((row) => ({ ...row })),
		stated: asArray(offerInfo._statedCampaignOffers)
			.filter((row) => row.productId !== productId)
			.map((row) => ({ ...row })),
	};
}

/** Fingerprint recorded in `_removedOfferInfoKeys`, so the offer is not recopied from a previous visit (lesson 81). */
export const removedOfferFingerprint = (offerInfo) => `${offerInfo.campaignId}_${offerInfo.campaignOfferId}_${isRealDate(offerInfo.startDate) ? new Date(offerInfo.startDate).getTime() : 0}`;

/**
 * The dates are frozen once the offer has entered its store period (`checkForReadonly`, JS:2414), unless its forecast is
 * still on (RG-24 : the offer then stays in the negotiation display, dates included).
 */
export function localOfferDatesEditable(snapshot, offerInfo, campaign = null) {
	if (snapshot?.readonly) return false;
	if (!hasStarted(snapshot, offerInfo)) return true;
	return !!campaign && forecastKeepsDatesEditable(snapshot, campaign, refOfOfferInfo(offerInfo));
}

const hasStarted = (snapshot, offerInfo) => isDateLess(offerInfo?.startDate, snapshot?.now, true);
const earliestDate = (...values) => values.filter(Boolean).sort()[0] || '';
const latestDate = (...values) => values.filter(Boolean).sort().pop() || '';

/**
 * Bounds of the date inputs (`getLocaleOfferInfo`, JS:1325) : start from tomorrow, both inside the campaign store period.
 * An offer already in its store period (dates kept editable by its forecast, RG-24) cannot end before today (RG-25).
 * Neither date goes past the statement end date (#23445) and the end stays strictly after the start (`hasValidOfferDates`) :
 * the date picker greys out every day `applyLocalOfferDate` would refuse.
 */
export function localOfferDateBounds(snapshot, campaign, offerInfo = null) {
	const storeBegin = toInputDate(campaign?.dates?.storeBegin);
	const storeEnd = toInputDate(campaign?.dates?.storeEnd);
	const statementEnd = toInputDate(offerInfo?.statementEndDate);
	const today = toInputDate(snapshot.now);
	const start = toInputDate(offerInfo?.startDate);
	const end = toInputDate(offerInfo?.endDate);
	return {
		start: { min: toInputDate(addDays(snapshot.now, 1)), max: earliestDate(storeEnd, statementEnd, end && toInputDate(addDays(offerInfo.endDate, -1))) },
		end: { min: latestDate(hasStarted(snapshot, offerInfo) && today > storeBegin ? today : storeBegin, start && toInputDate(addDays(offerInfo.startDate, 1))), max: earliestDate(storeEnd, statementEnd) },
	};
}

/**
 * Date change of a local offer (`updateLocaleOfferInfoHandler`, JS:3727). `rawValue` is the `yyyy-mm-dd` input value.
 * An emptied or unparseable date is persisted as '' (lesson 98) ; an end date not after the start is dropped.
 * Refused when it goes past the offer's statement end date (#23445), or, for an offer already in its store period, when the
 * end date is past (RG-25 : the offer would leave the step while its forecast is still on).
 */
export function applyLocalOfferDate(snapshot, campaign, offerInfo, field, rawValue) {
	if (!localOfferDatesEditable(snapshot, offerInfo, campaign)) return { ok: false, error: 'PromoReadonly' };
	const statementEnd = toInputDate(offerInfo.statementEndDate);
	if (statementEnd && rawValue && rawValue > statementEnd) return { ok: false, error: 'OfferStatementEndDateOutOfRange', params: [rawValue, campaign?.dates?.end] };
	if (field === 'endDate' && rawValue && hasStarted(snapshot, offerInfo) && rawValue < toInputDate(snapshot.now)) return { ok: false, error: 'PromoOfferEndDatePassed' };

	const parsed = rawValue ? new Date(rawValue) : null;
	const date = isRealDate(parsed) ? parsed : null;
	const patch = {};
	if (field === 'startDate') patch.startDate = date || '';
	else patch.endDate = date && isDateGreater(date, offerInfo.startDate, true) ? date : '';
	return { ok: true, offerInfo: patch };
}

/** Internal comment of a local offer, cut at 500 characters. */
export const applyLocalOfferComment = (value) => ({ ok: true, offerInfo: { comment: String(value || '').slice(0, 500) } });

/**
 * Rules of a local offer (`checkLocaleOfferRules`, JS:3840) : an empty offer, invalid dates, or a period overlapping
 * another occurrence of the same offer are blocking ; overlapping another offer is a warning. The INNOCENTFR temporary
 * exemption (JS:3834) expired on 30/09/2026 and is not ported.
 */
export function checkLocalOfferRules(snapshot, campaign, offerInfo) {
	let overlapping = false;
	let overlappingError = false;
	const emptyOffer = asArray(offerInfo?._negociatedCampaignOffers).length === 0;
	const datesOK = hasValidOfferDates(offerInfo);

	asArray(snapshot?.model?.offerInfos)
		.filter((oi) => oi.campaignId === offerInfo.campaignId && oi._key !== offerInfo._key)
		.forEach((other) => {
			if (overlappingError) return;
			const label = getOffer(campaign, other.campaignOfferId)?.label || other.campaignOfferId;
			const startInside = isDateGreater(offerInfo.startDate, other.startDate, true) && isDateLess(offerInfo.startDate, other.endDate, true);
			const endInside = isDateGreater(offerInfo.endDate, other.startDate, true) && isDateLess(offerInfo.endDate, other.endDate, true);
			if (startInside || endInside) {
				overlapping = label;
				overlappingError = other.campaignOfferId === offerInfo.campaignOfferId;
			}
		});

	return { overlapping, overlappingError, emptyOffer, datesOK, hasError: overlappingError || emptyOffer || !datesOK };
}
